/**
 * Invoice numbers are built from a per-company pattern:
 *
 *   {prefix}-{yyyy}-{seq:4}   INV-2026-0148      (the default)
 *   {prefix}{yyyy}{mm}{seq:3} RHU202609001
 *   {yyyy}{mm}{seq:4}         2026090019
 *
 * `{prefix}` is the company's invoice prefix, `{yyyy}` / `{yy}` and `{mm}`
 * come from the issue date, and `{seq}` is the running number, zero-padded to
 * the width after the colon (4 when omitted). Anything outside the braces is
 * printed as is. The running number starts over each year when the pattern
 * carries a year — a pattern without one keeps counting, or two years would
 * repeat numbers.
 */
export const DEFAULT_INVOICE_NUMBER_FORMAT = '{prefix}-{yyyy}-{seq:4}';

const TOKEN = /\{(prefix|yyyy|yy|mm|seq(?::([1-8]))?)\}/g;

export function formatInvoiceNumber(pattern: string, p: { prefix: string; date: string; seq: number }): string {
  const [yyyy = '', mm = ''] = p.date.split('-');
  return pattern.replace(TOKEN, (_, token: string, width?: string) => {
    if (token === 'prefix') return p.prefix;
    if (token === 'yyyy') return yyyy;
    if (token === 'yy') return yyyy.slice(2);
    if (token === 'mm') return mm;
    return String(p.seq).padStart(Number(width ?? 4), '0');
  });
}

/** A pattern resets its sequence yearly only when the year is part of the number. */
export function invoiceNumberResetsYearly(pattern: string): boolean {
  return /\{yyyy\}|\{yy\}/.test(pattern);
}

/** Why a pattern cannot be used, or null when it can. */
export function invoiceNumberFormatError(pattern: string): string | null {
  if (!pattern.trim()) return 'The pattern is empty';
  if (pattern.length > 60) return 'The pattern is longer than 60 characters';
  const unknown = pattern.replace(TOKEN, '').match(/\{[^}]*\}?|\}/);
  if (unknown) return `Unknown token ${unknown[0]}`;
  if (!/\{seq(?::[1-8])?\}/.test(pattern)) return 'The pattern needs a {seq} token for the running number';
  if ((pattern.match(/\{seq/g) ?? []).length > 1) return 'Only one {seq} token is allowed';
  if (/\s/.test(pattern)) return 'The pattern cannot contain spaces';
  return null;
}
